/**
 * HITL effective-tier resolver — ENG-5128 (parent ENG-5123).
 *
 * Computes the effective HITL tier for a single tool call by taking
 * the strictest value across four input layers:
 *
 *   1. catalog floor       — tool_definitions.min_hitl_tier
 *   2. integration bundle  — integration_definitions.defined_scopes[].tool_overrides
 *   3. agent install       — agent_integrations.tool_overrides
 *   4. CHARTER             — CHARTER.tools[].hitl_tier
 *
 * Strictness is by canonical ordinal (HITL_TIER_RANK), never lexical.
 * Lower-ranked overrides have no effect (the resolver picks the max);
 * a separate validator (hitl-validators.ts) rejects lower-than-floor
 * overrides at write time so payloads stay honest.
 *
 * ENG-10256 (ADR-0080 decision 5) adds a fifth possible outcome that is
 * NOT a tier: a per-tool `deny`, carried on layers 2 and 3, meaning the
 * call never runs and no approval is offered. It short-circuits, and the
 * return type is a discriminated union so no caller can read `.tier`
 * without first handling it.
 *
 * Two modes:
 *  - Fast path (default): returns the tier, chain stays empty. Used on
 *    the broker tool-call hot path; allocates nothing beyond the
 *    computed tier.
 *  - Explain path (`explain: true`): returns the tier AND the full
 *    contributing chain — every non-trivial layer with its source id
 *    and justification. Used by the install UI so the operator can
 *    see WHY a tool is at admin-tier ("because the integration
 *    definition raised it and you raised it further").
 *
 * See docs/prds/integration-mode-hitl-resolver.md for the design and
 * docs/prds/integration-mode.md for the parent PRD.
 */

import {
  HITL_TIER_ORDER,
  HITL_TIER_RANK,
  isToolHitlDeny,
  type HitlTier,
  type ToolHitlDeny,
  type ToolHitlOverride,
  type ToolHitlRaise,
} from '../types/integration.js';

export type ResolutionSource =
  | 'catalog'
  | 'integration_definition'
  | 'agent_install'
  | 'charter';

export interface ResolutionLayer {
  source: ResolutionSource;
  tier: HitlTier;
  /** Justification carried on the override (none for catalog floor / CHARTER). */
  justification?: string;
}

/**
 * The layer that denied the tool outright. Carries no tier — a terminal
 * refusal has no rank, which is the whole reason `deny` is a separate
 * verdict rather than a sixth member of `HITL_TIER_ORDER`.
 */
export interface DenyLayer {
  source: ResolutionSource;
  /** Prose from the override, shown to the operator and (redacted) the agent. */
  justification: string;
}

/**
 * Result of resolving one tool call.
 *
 * **A discriminated union on purpose.** `result.tier` does not compile
 * without narrowing on `verdict` first, so a caller physically cannot skip
 * the deny case — every consumer is forced to handle it at build time
 * rather than silently falling through to "no approval needed". That is
 * the failure this shape exists to make impossible: a denied tool that
 * executes because a gate compared an unfamiliar value against a set of
 * approval tiers and got `false`.
 */
export type EffectiveTier =
  | {
      verdict: 'tier';
      /** The strictest tier across all input layers. */
      tier: HitlTier;
      /**
       * Layers that contributed to the result. Empty in fast mode. In
       * explain mode, contains one entry per non-trivial input (catalog
       * floor is always present; overrides only appear when they raised
       * the tier above the previous layer).
       */
      chain: ResolutionLayer[];
    }
  | {
      verdict: 'deny';
      /** The layer that refused. */
      deniedBy: DenyLayer;
      /** Layers resolved before the deny short-circuited. */
      chain: ResolutionLayer[];
    };

export interface ResolveInput {
  /** The tool being resolved — used to look up overrides keyed by tool_key. */
  toolKey: string;
  /** Catalog floor for this tool (from `tool_definitions.min_hitl_tier`). */
  catalogFloor: HitlTier;
  /** Per-scope overrides on the integration_definition this install came from. */
  bundleOverrides?: ToolHitlOverride[];
  /** Per-agent install overrides on `agent_integrations.tool_overrides`. */
  installOverrides?: ToolHitlOverride[];
  /** Agent CHARTER ceiling for this tool, if declared. */
  charterTier?: HitlTier;
  /** When true, populates `chain` with the contributing layers. */
  explain?: boolean;
}

/**
 * Compare two tiers by canonical rank. Returns the stricter one (higher
 * rank). When tied, returns `a` — caller chooses which side to pass as
 * `a` to control preference.
 */
function strictest(a: HitlTier, b: HitlTier): HitlTier {
  return HITL_TIER_RANK[b] > HITL_TIER_RANK[a] ? b : a;
}

/**
 * Resolve every override on one layer that targets `toolKey` down to the
 * single one that governs.
 *
 * **Scans the whole array; deliberately not `.find()` (ENG-10277).** These
 * arrays carry no uniqueness constraint on `tool_key` — they are plain
 * JSONB — and a first-match lookup makes the result depend on array order.
 * With `[raise(X), deny(X)]` the raise was returned, the deny was never
 * inspected, and the tool routed to an Approve/Deny card as though the
 * operator had never denied it: a control that is stored, displayed and
 * inert, which is the phantom-control class ADR-0080 exists to close.
 *
 * Duplicates are not an operator mistake to be assumed away. The CLI lane
 * flattens `tool_overrides` across EVERY `agent_integrations` row for the
 * agent (`cli-broker/execute.ts`), so two installs each carrying one
 * override for the same tool collide by construction, with no duplicate
 * inside either row. On the bundle side `validateIntegrationManifest`
 * checks for duplicate entries in a scope's `tools[]` but not in its
 * `tool_overrides[]`, so two rows for one key pass structural validation.
 *
 * Precedence, in order:
 *
 *  1. **Any deny wins**, wherever it sits in the array. A deny is terminal,
 *     so no raise can be stricter than it and position cannot matter.
 *     The FIRST deny is the one returned, so the justification an operator
 *     is shown stays stable when several rows deny the same tool — they all
 *     mean never, and the outcome is identical whichever is reported.
 *  2. Otherwise the **strictest** raise by `HITL_TIER_RANK`, ties going to
 *     the earlier row (matching `strictest()`, which prefers its first
 *     argument). First-match here was the same defect one notch weaker:
 *     a lower raise ahead of a higher one under-enforced by a tier against
 *     a ceiling the operator had actually set.
 *
 * Rows that neither raise nor deny, and rows whose `raised_to` is not a
 * recognised tier, are skipped rather than returned. These values arrive
 * from JSONB where nothing enforces the union — reading `HITL_TIER_RANK` on
 * an absent tier would throw inside the resolution hot path, and an
 * unrecognised one ranks as `undefined`, which loses every comparison it
 * takes part in.
 */
function selectOverride(
  overrides: ToolHitlOverride[] | undefined,
  toolKey: string,
): ToolHitlOverride | undefined {
  if (!overrides) return undefined;

  let deny: ToolHitlDeny | undefined;
  let raise: ToolHitlRaise | undefined;

  for (const o of overrides) {
    if (o.tool_key !== toolKey) continue;
    if (isToolHitlDeny(o)) {
      deny ??= o;
      continue;
    }
    // An unrecognised tier has no rank, and every comparison against
    // `undefined` is false — so a garbage row reaching `raise` first would
    // make each later VALID raise lose, and the layer would resolve at the
    // catalog floor with the operator's real raise silently discarded
    // (CodeRabbit, PR 5831). Skipping it means a valid raise anywhere in the
    // array still wins, which can only make the outcome stricter or equal,
    // never looser.
    //
    // The bound, stated rather than implied: this does NOT make an
    // unparseable tier fail closed. A row that is the only override for its
    // tool still resolves to the catalog floor, exactly as before — nothing
    // here validates the string, and `validateToolOverrides` does not reject
    // it either, because its raise-above-floor arm compares
    // `HITL_TIER_RANK[unknown] <= floorRank`, i.e. `undefined <= n`, which is
    // false. What posture an uninterpretable tier deserves is a separate
    // question from this one, and is not answered here.
    const rank = o.raised_to ? HITL_TIER_RANK[o.raised_to] : undefined;
    if (rank === undefined) continue;
    if (!raise || rank > HITL_TIER_RANK[raise.raised_to]) {
      raise = o;
    }
  }

  return deny ?? raise;
}

export function resolveEffectiveTier(input: ResolveInput): EffectiveTier {
  const {
    toolKey,
    catalogFloor,
    bundleOverrides,
    installOverrides,
    charterTier,
    explain,
  } = input;

  const chain: ResolutionLayer[] = [];
  let tier: HitlTier = catalogFloor;

  if (explain) {
    chain.push({ source: 'catalog', tier: catalogFloor });
  }

  // A deny short-circuits. It is terminal, so nothing later can raise past
  // it and nothing may lower it — evaluating the remaining layers could
  // only produce a tier that is then discarded.
  //
  // Layer order decides which deny is REPORTED when more than one layer
  // denies the same tool: the bundle is checked before the install, so a
  // bundle deny is what the operator is shown. A second deny underneath is
  // redundant rather than conflicting — both mean never, and the outcome is
  // identical either way.
  const bundle = selectOverride(bundleOverrides, toolKey);
  if (bundle && isToolHitlDeny(bundle)) {
    return {
      verdict: 'deny',
      deniedBy: {
        source: 'integration_definition',
        justification: bundle.justification,
      },
      chain,
    };
  }

  if (bundle && bundle.raised_to) {
    const prev = tier;
    tier = strictest(tier, bundle.raised_to);
    if (explain && tier !== prev) {
      chain.push({
        source: 'integration_definition',
        tier: bundle.raised_to,
        justification: bundle.justification,
      });
    }
  }

  const install = selectOverride(installOverrides, toolKey);
  if (install && isToolHitlDeny(install)) {
    return {
      verdict: 'deny',
      deniedBy: { source: 'agent_install', justification: install.justification },
      chain,
    };
  }

  if (install && install.raised_to) {
    const prev = tier;
    tier = strictest(tier, install.raised_to);
    if (explain && tier !== prev) {
      chain.push({
        source: 'agent_install',
        tier: install.raised_to,
        justification: install.justification,
      });
    }
  }

  if (charterTier) {
    const prev = tier;
    tier = strictest(tier, charterTier);
    if (explain && tier !== prev) {
      chain.push({ source: 'charter', tier: charterTier });
    }
  }

  return { verdict: 'tier', tier, chain };
}

/** Convenience: is a tier strictly stricter than another? */
export function isStricter(candidate: HitlTier, baseline: HitlTier): boolean {
  return HITL_TIER_RANK[candidate] > HITL_TIER_RANK[baseline];
}

/** Convenience: is a tier auto-executable (no approval required in v1)? */
export function isAutoTier(tier: HitlTier): boolean {
  return tier === 'read' || tier === 'write';
}

/** Re-export so consumers don't need a second import for the tier vocabulary. */
export { HITL_TIER_ORDER, HITL_TIER_RANK };
export type { HitlTier };
